import React, { useState } from "react";
import DefaultCurrencyFormat from "../DefaultCurrencyFormat";
import { useTranslation } from "../../context/TranslationContext";
import { toIsoDate } from "../../helper/date";

const BoltIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.75a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.913-.143z"
      clipRule="evenodd"
    />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z"
      clipRule="evenodd"
    />
  </svg>
);

const XIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const SonDakika = ({ page, onSelect }) => {
  const {
    detailLastMinuteTitle,
    detailLastMinuteText,
    selectTheseDates,
    dateSelected,
    nights,
    clearSelection,
  } = useTranslation();

  const [selectedId, setSelectedId] = useState(null);

  const list = Array.isArray(page?.sonDakika) ? page.sonDakika : [];

  // Fiyat gizlenen (gavel) villalarda fırsat kutusu da gösterilmez
  if (!list.length || page?.gavel != "0") return null;

  const villaUrl = "/" + String(page.url || "").replace(/^\//, "");

  return (
    <div className="px-3 sm:px-[1.3rem] lg:px-0 mt-4 lg:mt-7">
      <div className="overflow-hidden bg-white border-2 lg:border-[3px] border-[#F7C9D6] rounded">
        <div className="flex items-center gap-2 px-4 py-3 text-white lg:px-7 bg-gradient-to-r from-[#E01252] to-[#FF7A45]">
          <BoltIcon className="w-5 h-5 shrink-0 animate-pulse" />
          <span className="text-base font-bold lg:text-lg">
            {detailLastMinuteTitle}
          </span>
          <span className="hidden ml-auto text-xs font-semibold opacity-90 lg:block">
            {detailLastMinuteText}
          </span>
        </div>

        <ul className="flex flex-col divide-y divide-[#F7E3E9]">
          {list.map((item) => {
            const start = toIsoDate(item.tarih1);
            const end = toIsoDate(item.tarih2);
            const href =
              start && end
                ? villaUrl + "?start=" + start + "&end=" + end
                : villaUrl;
            const isSelected = selectedId == item.id;

            // Tarihler formda seçilir, sayfa yenilenmez.
            // href sadece JS çalışmadığında / yeni sekmede açıldığında devreye girer.
            const SelectDates = (e) => {
              if (!onSelect || !start || !end) return;
              e.preventDefault();
              setSelectedId(item.id);
              onSelect(start, end);
            };

            const ClearDates = (e) => {
              e.preventDefault();
              setSelectedId(null);
              if (onSelect) onSelect(null, null);
            };

            return (
              <li
                key={item.id}
                className={
                  "flex flex-nowrap items-center gap-2 px-4 py-3 lg:gap-5 lg:px-7 lg:py-4 transition-colors " +
                  (isSelected ? "bg-[#FFF7F9]" : "")
                }
              >
                <div className="flex items-center gap-2 min-w-0 shrink lg:gap-5">
                  <div className="flex flex-col items-center justify-center w-12 h-12 lg:w-14 lg:h-14 shrink-0 rounded-md bg-[#FFF1F4] text-[#E01252] leading-none">
                    <strong className="text-lg lg:text-xl">{item.gun}</strong>
                    <span className="mt-0.5 text-xxs font-bold uppercase tracking-1 lg:mt-1">
                      {item.ay}
                    </span>
                  </div>

                  <div className="flex flex-col min-w-0 leading-tight">
                    <strong className="text-sm text-brand-black truncate lg:text-lg">
                      {item.gun1} - {item.gun2}
                    </strong>
                    <span className="text-xs font-semibold opacity-50 lg:text-13">
                      {item.gece} {nights}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1 ml-auto shrink-0 lg:flex-row lg:items-center lg:gap-5">
                  <div className="text-base font-bold lg:text-xl text-[#E01252]">
                    <DefaultCurrencyFormat
                      value={parseInt(item.fiyat)}
                      Symbol={item.Symbol || page.Symbol}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={href}
                      onClick={SelectDates}
                      title={page.baslik}
                      className={
                        "flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-md transition-colors whitespace-nowrap lg:px-4 lg:py-2.5 lg:text-sm " +
                        (isSelected
                          ? "bg-[#FFE0E9] text-[#E01252]"
                          : "bg-[#E01252] text-white hover:bg-[#c40f48]")
                      }
                    >
                      {isSelected && <CheckIcon className="w-4 h-4" />}
                      {isSelected ? dateSelected : selectTheseDates}
                    </a>
                    {isSelected && (
                      <button
                        type="button"
                        onClick={ClearDates}
                        title={clearSelection}
                        className="flex items-center justify-center w-8 h-8 lg:w-9 lg:h-9 rounded-md border-2 border-[#F7C9D6] bg-white text-[#E01252] hover:bg-[#E01252] hover:border-[#E01252] hover:text-white transition-all duration-200 shrink-0 shadow-sm"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default SonDakika;
